'use client';

import { type JSX, type ReactNode, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Icon, Tooltip } from '@videoblocks/design-system';

import { usePaneResize } from '../../_hooks/usePaneResize';

const MIN_WIDTH = 320;
const MAX_WIDTH = 600;
const DEFAULT_WIDTH = 420;

interface ChatPaneProps {
    children: ReactNode;
    /** Optional control rendered at the left of the header's icon group (e.g. the history button). */
    headerAction?: ReactNode;
}

/** Plus glyph for the "New Chat" action. Inherits color via `currentColor`. */
function PlusIcon(): JSX.Element {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-4 w-4">
            <path
                d="M0 8C0 8.26522 0.105357 8.51957 0.292893 8.70711C0.48043 8.89464 0.734784 9 1 9H6.83333C6.87754 9 6.91993 9.01756 6.95118 9.04882C6.98244 9.08007 7 9.12246 7 9.16667V15C7 15.2652 7.10536 15.5196 7.29289 15.7071C7.48043 15.8946 7.73478 16 8 16C8.26522 16 8.51957 15.8946 8.70711 15.7071C8.89464 15.5196 9 15.2652 9 15V9.16667C9 9.12246 9.01756 9.08007 9.04882 9.04882C9.08007 9.01756 9.12246 9 9.16667 9H15C15.2652 9 15.5196 8.89464 15.7071 8.70711C15.8946 8.51957 16 8.26522 16 8C16 7.73478 15.8946 7.48043 15.7071 7.29289C15.5196 7.10536 15.2652 7 15 7H9.16667C9.12246 7 9.08007 6.98244 9.04882 6.95118C9.01756 6.91993 9 6.87754 9 6.83333V1C9 0.734784 8.89464 0.48043 8.70711 0.292893C8.51957 0.105357 8.26522 0 8 0C7.73478 0 7.48043 0.105357 7.29289 0.292893C7.10536 0.48043 7 0.734784 7 1V6.83333C7 6.85522 6.99569 6.87689 6.98731 6.89711C6.97894 6.91733 6.96666 6.93571 6.95118 6.95118C6.93571 6.96666 6.91733 6.97894 6.89711 6.98731C6.87689 6.99569 6.85522 7 6.83333 7H1C0.734784 7 0.48043 7.10536 0.292893 7.29289C0.105357 7.48043 0 7.73478 0 8Z"
                fill="currentColor"
            />
        </svg>
    );
}

/**
 * Resizable chat pane container.
 * Renders a header with collapse functionality and a resizable panel.
 */
export function ChatPane({ children, headerAction }: ChatPaneProps): JSX.Element {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const { width, isDragging, handleMouseDown } = usePaneResize({
        minWidth: MIN_WIDTH,
        maxWidth: MAX_WIDTH,
        defaultWidth: DEFAULT_WIDTH,
    });

    if (isCollapsed) {
        return (
            <div className="w-12 h-full border-r border-gray-200 flex flex-col items-center py-4">
                <button
                    type="button"
                    onClick={() => setIsCollapsed(false)}
                    className="flex h-10 w-10 items-center justify-center rounded-sm hover:bg-gray-100 cursor-pointer transition duration-200"
                    aria-label="Expand chat"
                >
                    <Icon name="panelExpand" className="w-6 h-6 text-gray-600" />
                </button>
            </div>
        );
    }

    return (
        <div className="relative h-full flex" style={{ width: `${width}px` }}>
            {/* Main pane content */}
            <div className="flex-1 h-full border-r border-gray-200 flex flex-col bg-white">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 h-[--spacing(14.25)]">
                    <h1 className="text-base font-semibold text-gray-900">Conversational Search</h1>
                    <div className="flex items-center gap-2">
                        {headerAction}
                        <Tooltip
                            title="New Chat"
                            contentProps={{ side: 'bottom' }}
                            trigger={
                                <a
                                    aria-label="Start a New Chat"
                                    href="/ai/conv-search/new"
                                    className="flex h-10 w-10 items-center justify-center rounded-sm text-gray-600 hover:bg-gray-100 transition duration-200"
                                >
                                    <PlusIcon />
                                </a>
                            }
                        />
                        <button
                            type="button"
                            onClick={() => setIsCollapsed(true)}
                            className="flex h-10 w-10 items-center justify-center rounded-sm hover:bg-gray-100 cursor-pointer transition duration-200"
                            aria-label="Collapse chat"
                        >
                            <Icon name="panelCollapse" className="w-6 h-6 text-gray-600" />
                        </button>
                    </div>
                </div>

                {/* Chat content - passed as children */}
                <div className="flex-1 overflow-hidden">{children}</div>
            </div>

            {/* Resize handle */}
            <div
                className={twMerge(
                    'absolute right-0 top-0 bottom-0 w-1 cursor-col-resize transition-colors',
                    isDragging ? 'bg-blue-400' : 'hover:bg-blue-400',
                )}
                onMouseDown={handleMouseDown}
            />
        </div>
    );
}
